/**
 * src/pages/InformeSlaPrint.jsx
 * Vista imprimible del informe mensual de SLA (?mes=YYYY-MM&cliente=slug).
 * Pensada para "Guardar como PDF" del navegador: documento con marca,
 * sin sidebar ni controles. Si se pasa ?cliente=, informe individual
 * (el formato que se envía a un cliente).
 */
import { useSearchParams } from 'react-router-dom'
import { useSlaInforme } from '../hooks/useSla'

function fmtMes(mes) {
  if (!mes) return ''
  const [y, m] = mes.split('-').map(Number)
  const s = new Date(y, m - 1, 1).toLocaleDateString('es-CL', { month: 'long', year: 'numeric' })
  return s.charAt(0).toUpperCase() + s.slice(1)
}

const celda = {
  padding: '9px 12px', borderBottom: '1px solid #ddd',
  fontSize: '13px', textAlign: 'left',
}
const cabecera = {
  ...celda, fontSize: '11px', letterSpacing: '0.06em', textTransform: 'uppercase',
  color: '#666', borderBottom: '2px solid #333',
}

export default function InformeSlaPrint() {
  const [params] = useSearchParams()
  const mes = params.get('mes') || ''
  const clienteSel = params.get('cliente') || ''
  const { data, isLoading } = useSlaInforme(mes)

  const filas = (data?.clientes || []).filter(
    c => !clienteSel || c.cliente_id === clienteSel
  )
  const individual = clienteSel && filas.length === 1 ? filas[0] : null

  return (
    <div style={{
      maxWidth: '860px', margin: '0 auto', padding: '40px 32px',
      fontFamily: 'var(--font-ui)', color: '#1a1a1a', background: '#fff',
      minHeight: '100vh',
    }}>
      <style>{`
        @media print { .no-print { display: none !important; } }
        @page { margin: 18mm; }
      `}</style>

      {/* Controles (no salen en el PDF) */}
      <div className="no-print" style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '24px', padding: '10px 14px',
        background: '#f4f6f8', border: '1px solid #ddd', borderRadius: '8px',
      }}>
        <span style={{ fontSize: '13px', color: '#555' }}>
          Vista previa del informe — usa «Imprimir» y elige «Guardar como PDF»
        </span>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => window.history.back()} style={{
            border: '1px solid #ccc', background: '#fff', borderRadius: '6px',
            padding: '6px 14px', cursor: 'pointer', fontSize: '13px',
          }}>← Volver</button>
          <button onClick={() => window.print()} style={{
            border: 'none', background: '#FF5E3A', color: '#fff', borderRadius: '6px',
            padding: '6px 16px', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
          }}>Imprimir / Guardar PDF</button>
        </div>
      </div>

      {/* Cabecera del documento */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
        borderBottom: '3px solid #FF5E3A', paddingBottom: '14px', marginBottom: '24px',
      }}>
        <div>
          <div style={{ fontSize: '22px', fontWeight: 700, letterSpacing: '-0.02em' }}>
            CM Monitor
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
            Monitorización de precios · Convenio Marco · Mercado Público
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '15px', fontWeight: 600 }}>
            Informe de nivel de servicio (SLA)
          </div>
          <div style={{ fontSize: '13px', color: '#666' }}>
            {fmtMes(data?.mes)}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>Cargando…</div>
      ) : (
        <>
          {individual && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '17px', fontWeight: 600 }}>{individual.nombre}</div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                Cliente: {individual.cliente_id} · Plan {individual.tier}
              </div>
            </div>
          )}

          {/* Compromiso */}
          <div style={{
            background: '#f9fafb', border: '1px solid #e3e6ea', borderRadius: '8px',
            padding: '14px 18px', marginBottom: '24px', fontSize: '13px', lineHeight: 1.55,
          }}>
            <strong>Compromiso de servicio:</strong> datos de precios actualizados con menos
            de {data?.umbral_fresco_h ?? 26} horas de antigüedad
            el {data?.objetivo_frescura_pct ?? 99} % de los días del mes, y disponibilidad
            del panel del 99,5 % mensual (vigilada de forma independiente por Better Stack).
          </div>

          {/* Resultados */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '24px' }}>
            <thead>
              <tr>
                {!individual && <th style={cabecera}>Cliente</th>}
                <th style={cabecera}>Días con datos frescos</th>
                <th style={cabecera}>Cumplimiento</th>
                <th style={cabecera}>Ejecuciones correctas</th>
                <th style={cabecera}>Incidencias</th>
              </tr>
            </thead>
            <tbody>
              {filas.map(c => {
                const totalRuns = c.runs_ok + c.runs_error
                return (
                  <tr key={c.cliente_id}>
                    {!individual && (
                      <td style={celda}>
                        <div style={{ fontWeight: 600 }}>{c.nombre}</div>
                        <div style={{ fontSize: '11px', color: '#888' }}>{c.cliente_id}</div>
                      </td>
                    )}
                    <td style={celda}>
                      {c.dias_con_datos} de {c.dias_evaluables} ({c.pct_frescura} %)
                    </td>
                    <td style={{
                      ...celda, fontWeight: 700,
                      color: c.cumple_sla ? '#15803d' : '#dc2626',
                    }}>
                      {c.cumple_sla ? '✓ CUMPLE' : '✗ NO CUMPLE'}
                    </td>
                    <td style={celda}>
                      {totalRuns
                        ? `${c.runs_ok} de ${totalRuns} (${c.pct_exito_runs} %)`
                        : 'sin ejecuciones'}
                    </td>
                    <td style={celda}>{c.incidencias}</td>
                  </tr>
                )
              })}
              {filas.length === 0 && (
                <tr><td style={celda} colSpan={5}>Sin datos para este mes</td></tr>
              )}
            </tbody>
          </table>

          <div style={{ fontSize: '11px', color: '#888', lineHeight: 1.6 }}>
            «Días con datos frescos» cuenta los días del mes con al menos una actualización
            correcta de precios{data?.dias_evaluables ? ` (mes evaluado sobre ${data.dias_evaluables} días)` : ''}.
            Las incidencias corresponden a avisos de severidad alta registrados por el sistema
            de monitorización y atribuidos al cliente.
            <br />
            Informe generado automáticamente por CM Monitor el {new Date().toLocaleDateString('es-CL')} · cmmonitor.cl
          </div>
        </>
      )}
    </div>
  )
}
